import React, { useState } from 'react';
import { Button, Col, DatePicker, Form, InputNumber, Modal, Select, Table, Tag, Typography } from 'antd';
import { TextFormat } from 'react-jhipster';
import { APP_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { getMoyenPaiementColor, getStatusColor } from 'app/utils/colorUtils';
import { getPaymentMethod } from 'app/utils/paymentUtils';
import { FactureDetails, StatutFacture } from 'app/shared/model/facture.model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSave } from '@fortawesome/free-solid-svg-icons';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Row } from 'reactstrap';
import { resendFacture } from '../factures/facture.reducer';
import { Link } from 'react-router-dom';
import SolderFactureModal from './SolderFactureModal';
import { StatusAnneeScolaire } from 'app/shared/model/anneeScolaire.model';

const { Title } = Typography;

interface DetailsFactureProps {
  facture: FactureDetails;
  frais: number;
  anneeScolaireId: string;
  handleMontantEdit: () => void;
  handleStatutEdit: () => void;
  handleConfirm: () => void;
  handleDisable: () => boolean;
  statutFacture: StatutFacture;
  setStatutFacture: (value: React.SetStateAction<StatutFacture>) => void;
  editingStatut: boolean;
  editingMontantNet: boolean;
  montantNet: number;
  setMontantNet: React.Dispatch<React.SetStateAction<number>>;
  inactiveAnnee: boolean;
}

const DetailsFacture: React.FC<DetailsFactureProps> = ({
  facture,
  frais,
  editingStatut,
  handleMontantEdit,
  handleStatutEdit,
  statutFacture,
  setStatutFacture,
  handleConfirm,
  montantNet,
  setMontantNet,
  editingMontantNet,
  anneeScolaireId,
  inactiveAnnee,
  handleDisable,
}) => {
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isComptable = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.COMPTABLE]));
  const isDirecteur = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.DIRECTEUR]));
  const hasAccess = isAdmin || isDirecteur || isComptable;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const showSoldePayModal = () => {
    setIsModalVisible(true);
  };
  const columns = [
    { title: '', dataIndex: 'label' },
    { title: '', dataIndex: 'value' },
  ];

  const data = [
    { key: '1', label: 'Numéro de Facture', value: <span>{facture.numeroFacture}</span> },
    {
      key: '2',
      label: 'Elève',
      value: (
        <span>
          <Link to={`/eleve/${facture.ecoleId}/${anneeScolaireId}/${facture.eleveId}/details`}>
            <strong>{`${facture.eleve}`}</strong>
          </Link>
        </span>
      ),
    },
    {
      key: '3',
      label: 'Statut',
      value: editingStatut ? (
        <span className="space-button">
          <Select
            defaultValue={statutFacture}
            style={{ width: '70%', marginRight: '8px' }}
            onChange={value => {
              setStatutFacture(value);
            }}
          >
            <Select.Option value="PAYEE" label="PAYEE">
              <Tag color="green">Payée</Tag>
            </Select.Option>
            <Select.Option value="EN_ATTENTE" label="EN_ATTENTE">
              <Tag color="red">En attente</Tag>
            </Select.Option>
            <Select.Option value="CREE" label="CREE">
              <Tag color="#999">Créee</Tag>
            </Select.Option>
            <Select.Option value="EMISE" label="EMISE">
              <Tag color="blue">Envoyéee</Tag>
            </Select.Option>
          </Select>
          <Button type="primary" onClick={handleConfirm} style={{ marginRight: '4px' }}>
            Confirmer
          </Button>
          <Button onClick={handleStatutEdit}>Annuler</Button>
        </span>
      ) : (
        <div className="space-button">
          <span style={{ color: getStatusColor(facture.statutFacture), fontWeight: facture.statutFacture ? 'bold' : '' }}>
            {facture.statutFacture === StatutFacture.PAYEE
              ? 'Payée'
              : facture.statutFacture === StatutFacture.EMISE
              ? 'Envoyée'
              : facture.statutFacture === StatutFacture.EN_ATTENTE
              ? 'En Attente'
              : 'Créée'}
          </span>

          {(isAdmin || isDirecteur) &&
            facture.statutFacture === StatutFacture.EMISE &&
            facture.datePaiement != null &&
            facture.moyenPaiement != null && (
              <Button
                className="button-right"
                style={{ background: 'red' }}
                type="primary"
                icon={<FontAwesomeIcon icon={faEdit} />}
                onClick={handleStatutEdit}
              ></Button>
            )}
        </div>
      ),
    },
    {
      key: '4',
      label: 'Frais',
      value: (
        <span>
          {facture.moyenPaiement !== 'ESPECE_CHECK'
            ? `${facture.statutFacture === StatutFacture.PAYEE ? facture.frais : frais} CFA`
            : '0 CFA'}
        </span>
      ),
    },
    {
      key: '5',
      label: 'Montant Net',
      value: editingMontantNet ? (
        <span className="space-button">
          <InputNumber value={montantNet} onChange={value => setMontantNet(value)} style={{ width: '80%', marginRight: '8px' }} />
          <Button type="primary" onClick={handleConfirm} style={{ marginRight: '4px' }}>
            Confirmer
          </Button>
          <Button onClick={handleMontantEdit}>Annuler</Button>
        </span>
      ) : (
        <div className="space-button">
          <span>{`${Number(facture.montant)} CFA`}</span>
          {hasAccess && (facture.statutFacture === 'CREE' || facture.statutFacture === StatutFacture.EMISE) && (
            <Button
              disabled={handleDisable()}
              className="button-right"
              type="primary"
              onClick={handleMontantEdit}
              icon={<FontAwesomeIcon icon={faEdit} />}
            ></Button>
          )}
        </div>
      ),
    },
    {
      key: '6',
      label: 'Montant Total',
      value: (
        <span>
          {facture.moyenPaiement === 'ESPECE_CHECK'
            ? `${facture.montant} CFA`
            : `${facture.montant + (facture.statutFacture === 'PAYEE' ? Number(facture.frais) : Number(frais))} CFA`}
        </span>
      ),
    },
    { key: '7', label: "Date d'Émission", value: <TextFormat type="date" value={facture.dateEmission} format={APP_DATE_FORMAT} /> },
    {
      key: '8',
      label: 'Date de Paiement',
      value: facture.datePaiement ? <TextFormat type="date" value={facture.datePaiement} format={APP_DATE_FORMAT} /> : 'En Attente',
    },
    {
      key: '9',
      label: 'Moyen de Paiement',
      value: (
        <span style={{ color: getMoyenPaiementColor(facture.moyenPaiement), fontWeight: facture.moyenPaiement ? 'bold' : '' }}>
          {facture.moyenPaiement ? getPaymentMethod(facture.moyenPaiement) : 'En Attente'}
        </span>
      ),
    },
  ];

  return (
    <>
      <Table
        sticky
        showHeader={false}
        title={() => <TableTitle facture={facture} inactiveAnnee={inactiveAnnee} handleDisable={handleDisable} />}
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        rowClassName="facture-table"
      />
    </>
  );
};

export default DetailsFacture;

const TableTitle: React.FC<{ facture: FactureDetails; inactiveAnnee: boolean; handleDisable: () => boolean }> = ({
  facture,
  inactiveAnnee,
  handleDisable,
}) => {
  const dispatch = useAppDispatch();
  const [sendingFacture, setSendingFacture] = useState(false);
  const showConfirmModal = () => {
    const confirmModal = Modal.confirm({
      title: 'Confirmation',
      content: 'Voulez-vous vraiment renvoyer cette facture au parent?',
      okText: 'Oui',
      cancelText: 'Non',
      async onOk() {
        const loadingModal = Modal.info({
          title: 'Veuillez patienter',
          content: 'Envoi en cours. Merci de patienter...',
          closable: false,
          okText: 'Fermer',
          onOk() {
            loadingModal.destroy();
            confirmModal.destroy();
          },
        });

        try {
          setSendingFacture(true);

          await dispatch(resendFacture(facture.factureId));
          setSendingFacture(false);
          loadingModal.destroy();
          confirmModal.destroy();
        } catch (error) {
          loadingModal.destroy();
          confirmModal.destroy();
          Modal.error({
            title: "Erreur lors de l'envoi de la facture",
            content: "Une erreur s'est produite lors de l'envoi de la facture. Veuillez réessayer.",
            okText: 'Fermer',
          });
        }
      },
      onCancel() {
        confirmModal.destroy();
      },
    });
  };

  return (
    <Row>
      <Col>
        <Title level={5}>
          Facture N° <span className="invoice-title">{facture.numeroFacture}</span>
        </Title>
      </Col>
      <Col offset={8}>
        <Button
          onClick={showConfirmModal}
          disabled={sendingFacture || handleDisable()}
          style={{ backgroundColor: '#f28e19', color: 'white', fontWeight: 'bold' }}
        >
          Renvoyer la facture
        </Button>
        {facture.statutFacture !== StatutFacture.PAYEE && (
          <SolderFactureModal handleDisable={handleDisable} inactiveAnnee={inactiveAnnee} facture={facture.factureId} />
        )}
      </Col>
    </Row>
  );
};
